import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api-client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Spinner } from '@/components/ui/Spinner';
import { Table, Th, Td } from '@/components/ui/Table';
import { useUiStore } from '@/stores/uiStore';

type FieldConfig = {
  field: string;
  visible: boolean;
  required: boolean;
  label: { ro: string; ru: string; en: string };
};

export function SettingsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const language = useUiStore((s) => s.language);
  const queryClient = useQueryClient();

  const [localFields, setLocalFields] = useState<FieldConfig[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'photo-fields'],
    queryFn: () =>
      api.get('settings/photo-fields').json<{ fields: FieldConfig[] }>(),
  });

  useEffect(() => {
    if (data?.fields) {
      setLocalFields(data.fields);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (fields: FieldConfig[]) =>
      api.put('settings/photo-fields', { json: fields }).json<{ fields: FieldConfig[] }>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'photo-fields'] });
      pushToast({ message: t('settings.saveSuccess'), variant: 'success' });
    },
    onError: async (err) => {
      const { message } = await apiError(err);
      pushToast({ message, variant: 'error' });
    },
  });

  const toggleVisible = (index: number) => {
    setLocalFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, visible: !f.visible } : f)),
    );
  };

  const toggleRequired = (index: number) => {
    setLocalFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, required: !f.required } : f)),
    );
  };

  const handleSave = () => {
    mutation.mutate(localFields);
  };

  const getLabel = (field: FieldConfig): string => {
    return field.label[language] || field.label.ro;
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{t('settings.title')}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('settings.subtitle')}</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('settings.title')}
            </span>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? <Spinner /> : null}
              {t('settings.save')}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex justify-center p-10">
              <Spinner />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t('settings.field')}</Th>
                  <Th>{t('settings.visible')}</Th>
                  <Th>{t('settings.required')}</Th>
                </tr>
              </thead>
              <tbody>
                {localFields.map((field, index) => (
                  <tr key={field.field}>
                    <Td className="font-medium">{getLabel(field)}</Td>
                    <Td>
                      <Checkbox
                        checked={field.visible}
                        onChange={() => toggleVisible(index)}
                      />
                    </Td>
                    <Td>
                      <Checkbox
                        checked={field.required}
                        onChange={() => toggleRequired(index)}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
